import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import ApprovalDialog from "@/components/ApprovalDialog";
import { Tag, MessageSquare, ShieldCheck, Loader2 } from "lucide-react";

export default function WholesaleAuthDialog({ open, customer, onClose, onAuthorized }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [devCode, setDevCode] = useState("");
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [supOpen, setSupOpen] = useState(false);
  const [mode, setMode] = useState("both");
  useEffect(() => { if (open) api.get("/wholesale/mode").then((r) => setMode(r.data.mode || "both")).catch(() => setMode("both")); }, [open]);

  const close = () => { setSent(false); setDevCode(""); setCode(""); onClose(); };

  const sendOtp = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/wholesale/otp/send", { customer_id: customer.id });
      setSent(true);
      if (data.dev_code) { setDevCode(data.dev_code); toast.info("SMS yapılandırılmadı — kod ekranda (test)"); }
      else toast.success(`Kod ${data.masked_phone || "müşteri telefonuna"} gönderildi`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Kod gönderilemedi"); }
    finally { setSending(false); }
  };

  const verifyOtp = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post("/wholesale/otp/verify", { customer_id: customer.id, code: code.trim() });
      onAuthorized({ method: "sms", token: data.token, by: customer.name, at: new Date().toISOString() });
      toast.success("Toptan alışveriş yetkilendirildi (SMS)");
      close();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Kod hatalı"); }
    finally { setVerifying(false); }
  };

  if (!customer) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="sm:max-w-md" data-testid="wholesale-auth-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Tag className="h-5 w-5 text-brand-green" /> Toptan (Fırsatlı) Satış Yetkisi</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-600"><b>{customer.name}</b> toptan alışverişe uygun. Toptan fiyatlarla başlamak için doğrulama gerekir.</p>

          <div className="rounded-lg border border-slate-200 p-3">
            <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-700"><MessageSquare className="h-4 w-4" /> Müşteri SMS kodu</p>
            {!sent ? (
              <Button data-testid="wholesale-send-otp" onClick={sendOtp} disabled={sending} className="w-full bg-brand-green text-white hover:bg-brand-green-600">
                {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Müşteri telefonuna kod gönder
              </Button>
            ) : (
              <div className="space-y-2">
                {devCode ? <p data-testid="wholesale-dev-code" className="rounded bg-amber-50 px-2 py-1 text-center text-sm font-bold tracking-widest text-amber-700">{devCode}</p> : null}
                <Input data-testid="wholesale-otp-input" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6 haneli kod" inputMode="numeric" className="text-center text-lg tracking-widest" />
                <div className="flex gap-2">
                  <Button variant="outline" onClick={sendOtp} disabled={sending} className="flex-1">Tekrar gönder</Button>
                  <Button data-testid="wholesale-verify-otp" onClick={verifyOtp} disabled={verifying || code.length < 4} className="flex-1 bg-brand-green text-white hover:bg-brand-green-600">{verifying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Doğrula</Button>
                </div>
              </div>
            )}
          </div>

          {mode === "both" && <div className="relative py-1 text-center text-[11px] text-slate-400"><span className="relative z-10 bg-white px-2">veya</span><div className="absolute left-0 top-1/2 h-px w-full bg-slate-200" /></div>}

          {mode !== "sms" && (
          <Button data-testid="wholesale-supervisor-btn" variant="outline" onClick={() => setSupOpen(true)} className="w-full border-brand-green/40 text-brand-green hover:bg-brand-green/5">
            <ShieldCheck className="mr-2 h-4 w-4" /> Amir onayı (Müdür / Bölge / Asistan / Yönetici)
          </Button>
          )}

          <button data-testid="wholesale-cancel" onClick={close} className="w-full text-center text-xs text-slate-400 hover:text-slate-600">Vazgeç — perakende fiyatla devam et</button>
        </div>

        <ApprovalDialog open={supOpen} onClose={() => setSupOpen(false)} permission="sale.wholesale.approve"
          title="Toptan Fiyat Onayı" description="Bu müşteriye toptan (fırsatlı) fiyat uygulamak için yetkili onayı."
          onApproved={async (cred) => {
            try {
              const { data } = await api.post("/wholesale/authorize", { customer_id: customer.id, ...cred });
              onAuthorized({ method: "supervisor", token: data.token, by: data.by, role: data.role, at: new Date().toISOString() });
              toast.success("Toptan alışveriş yetkilendirildi (amir onayı)");
              close();
            } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Amir onayı doğrulanamadı"); }
          }} />
      </DialogContent>
    </Dialog>
  );
}
